/**
 * ESP destinations section — the ONE home of the brand-list catalog and the
 * section that renders it (epic 101 fix 06, ruling D7).
 *
 * Extracted from the campaign form's 4 000-line monolith so a connector-list
 * change lands in this file and applies to every style that renders the
 * section — the operator's copy-paste concern (card t_20200753, 2026-10-19).
 * D7: ESP destinations are a property of the three journey styles
 * (`paid_social` / `cto_email` / `external_form`) only; Co-op left in fix 06
 * (the original request's Co-op placement was an error). The form gates the
 * render with its `hasEspDestinations` flag; this component owns the catalog
 * (`BRAND_LIST_OPTIONS` / `LEGACY_BRAND_LIST_OPTIONS` / `BEEHIIV_PUB_IDS`),
 * the offeredFor filter, the Iterable warn banner and the whole table +
 * Add/Remove/List-id/transactional controls. No brand-list data may be
 * duplicated elsewhere in the client.
 *
 * Stateless by design: the rows live in the form's state slice and come back
 * through `onRowsChange` (plus `onTouched` for the seed latch), so the form
 * keeps owning hydration, seeding (D4) and the payload build.
 */

import { Fragment } from 'react';
import type { BrandList, EspProvider } from '@leads-platform/shared';
import { Button } from './ui/button';
import { FormSection } from './ui/form-section';
import { Input } from './ui/input';
import { Select } from './ui/select';
import { Banner } from './ui/banner';
import type { CampaignStyleEnumValue } from '../lib/campaign-styles';

// ---------------------------------------------------------------------------
// ESP destination row (form-state shape)
// ---------------------------------------------------------------------------

export interface EspDestinationRow {
  id: string;
  provider: string;
  brandList: string;
  listIdentifier: string;
  fieldMappingsJson: string;
  // A12 (034-05): transactional pair, Maropost rows only. Optional so existing
  // FormState builders (campaign-form.preview.test.tsx) compile without change;
  // EMPTY_FORM + every Add action always set them, so the running form never
  // sees them undefined.
  transactionalCampaignId?: string;
  transactionalOn?: '' | 'success' | 'valid';
  // 067-06 (D6): the Maropost TT row's journey trigger + tags, stored in
  // fieldMappings (withMaropostTTConfig). Optional for the same reason as the
  // transactional pair; hydration, the seed and every Add set them.
  maropostJourneyId?: string;
  maropostTriggerUid?: string;
  /** Comma-separated as typed; saved as a trimmed, de-duplicated array. */
  maropostTags?: string;
}

// ---------------------------------------------------------------------------
// Catalog (the only copy in the client)
// ---------------------------------------------------------------------------

// Provider+brandList rows of the ESP destinations catalog. provider/brandList
// strings must be values of espProviderEnum / brandListEnum
// (packages/shared/src/domain/enums.ts) — the payload build in aggregateFromForm
// casts them to the wire union, and the api persists them under DB CHECK
// constraints that mirror the same enums (three-way consistency).
// Catalog = the four brand lists Chelsie uses (QA-04, 2026-09-25, HF-075):
// TT, TWM, SIS, TSI — plus the three beehiiv publications of epic 101 (twm,
// sis, fsa), offered to all three journey styles (paid_social / cto_email /
// external_form) via offeredFor (D8 — operator QA round 2, 2026-10-19: CTO
// uses exactly the Paid Social set; amends D7, which had kept cto_email
// excluding them; Co-op left out in fix 06; absent = offered everywhere the
// section shows). DMB and Super Investors are no longer offered for new rows
// — BRD FR-B1c's addendum calls them "later promotions" — but a campaign
// that already has one still shows it via LEGACY_BRAND_LIST_OPTIONS below,
// so it can be seen and removed without silently orphaning saved state.
// defaultListIdentifier: the three Iterable journey ids Manny gave 2026-09-03,
// ratified by operator ruling R2 (067 README D5) — Adding a row fills its id so
// a campaign can no longer look configured while silently skipping Iterable
// (missing_list_identifier). The three beehiiv rows carry their publication
// ids the same way (epic 101 D5); BEEHIIV_PUB_IDS is the only home of those
// literals (never re-typed, never env config).
export const BEEHIIV_PUB_IDS = {
  twm: 'pub_b33e6f90-03c6-4e77-b777-dc41f74d50a7',
  sis: 'pub_7a631194-d096-43d5-ab10-f374569693a1',
  fsa: 'pub_a4d0f0b3-1d9b-434b-82cd-57a61db3fa79',
} as const;

const BRAND_LIST_OPTIONS = [
  { provider: 'maropost', brandList: 'tt', label: 'TT' },
  { provider: 'iterable', brandList: 'twm', label: 'TWM', defaultListIdentifier: '378266' },
  { provider: 'iterable', brandList: 'sis', label: 'SIS', defaultListIdentifier: '262844' },
  { provider: 'beehiiv', brandList: 'thesmartinvestor', label: 'The Smart Investor (Beehiiv)' },
  {
    provider: 'beehiiv',
    brandList: 'twm',
    label: 'Trading With Manny (Beehiiv)',
    defaultListIdentifier: BEEHIIV_PUB_IDS.twm,
    offeredFor: ['paid_social', 'cto_email', 'external_form'],
  },
  {
    provider: 'beehiiv',
    brandList: 'sis',
    label: 'Smart Investing Society (Beehiiv)',
    defaultListIdentifier: BEEHIIV_PUB_IDS.sis,
    offeredFor: ['paid_social', 'cto_email', 'external_form'],
  },
  {
    provider: 'beehiiv',
    brandList: 'fsa',
    label: 'Free Stock Alerts (Beehiiv)',
    defaultListIdentifier: BEEHIIV_PUB_IDS.fsa,
    offeredFor: ['paid_social', 'cto_email', 'external_form'],
  },
] satisfies {
  provider: EspProvider;
  brandList: BrandList;
  label: string;
  defaultListIdentifier?: string;
  offeredFor?: CampaignStyleEnumValue[];
}[];

// No longer offered for new rows (HF-075, QA-04) — kept only so a campaign
// that already has one of these rows can still render, be seen and be
// removed. Never rendered as an Add candidate. DMB keeps its Iterable
// defaultListIdentifier for the day it is offered again (out of scope: the
// Iterable per-brand keys and the DMB journey id stay wired).
const LEGACY_BRAND_LIST_OPTIONS = [
  { provider: 'iterable', brandList: 'dmb', label: 'DMB', defaultListIdentifier: '683412' },
  { provider: 'beehiiv', brandList: 'superinvestors', label: 'Super Investors (Beehiiv)' },
] satisfies {
  provider: EspProvider;
  brandList: BrandList;
  label: string;
  defaultListIdentifier?: string;
}[];

// ---------------------------------------------------------------------------
// D4 journey seed (reads identifiers off the catalog above — never re-typed)
// ---------------------------------------------------------------------------

// Q107 (answered on the Sep-15 call — "maybe we can have those automatically
// selected … those aren't changing") + BRD FR-B23 (Manny 2026-09-09), widened
// by HF-085: Chelsie, 2026-09-25 — "All List IDs and Transactional IDs need
// to be prefilled/defaulted for each CTO link and Paid Social page" — a NEW
// journey campaign starts with all four brand lists she uses (QA-04): Maropost
// TT list 8, Iterable TWM, Iterable SIS, Beehiiv The Smart Investor (no
// identifier — matches production rows 32/33/39 and BRAND_LIST_OPTIONS above).
// This supersedes ruling R2 ("rows are not pre-added for every brand") for
// these two styles only. Seeded in create mode only by the form's effect; the
// rows stay editable and removable per campaign. The default lives here —
// change it here, never in a migration (per-campaign table, no seeded
// esp_destination row). The Iterable/Beehiiv identifiers are read off
// BRAND_LIST_OPTIONS rather than re-typed, so the two catalogs cannot drift;
// Maropost TT's '8' is a journey-seed default, not a catalog default, so it is
// not in BRAND_LIST_OPTIONS and is given explicitly.
function seedRowFromCatalog(
  provider: EspProvider,
  brandList: BrandList
): { provider: EspProvider; brandList: BrandList; listIdentifier: string } {
  const catalogRow = BRAND_LIST_OPTIONS.find(
    (bl) => bl.provider === provider && bl.brandList === brandList
  );
  return { provider, brandList, listIdentifier: catalogRow?.defaultListIdentifier ?? '' };
}

const ESP_DEFAULT_DESTINATION_ROWS: Array<{
  provider: EspProvider;
  brandList: BrandList;
  listIdentifier: string;
}> = [
  { provider: 'maropost', brandList: 'tt', listIdentifier: '8' },
  seedRowFromCatalog('iterable', 'twm'),
  seedRowFromCatalog('iterable', 'sis'),
  seedRowFromCatalog('beehiiv', 'thesmartinvestor'),
];

export const ESP_DEFAULT_DESTINATIONS: Record<
  'paid_social' | 'cto_email',
  Array<{ provider: EspProvider; brandList: BrandList; listIdentifier: string }>
> = {
  paid_social: ESP_DEFAULT_DESTINATION_ROWS,
  cto_email: ESP_DEFAULT_DESTINATION_ROWS,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

let rowCounter = 0;
const newRowId = () => `esp-row-${++rowCounter}`;

interface EspDestinationsSectionProps {
  /** The campaign's wire style — drives the offeredFor Add-candidates filter. */
  campaignStyle: CampaignStyleEnumValue;
  /** The form's espDestinations state slice, verbatim. */
  espDestinations: EspDestinationRow[];
  /** Emits the next full row array (Add / Remove / List id / transactional). */
  onRowsChange: (rows: EspDestinationRow[]) => void;
  /** Fired before any manual edit — latches the form's seed off (054-08). */
  onTouched: () => void;
}

export function EspDestinationsSection({
  campaignStyle,
  espDestinations,
  onRowsChange,
  onTouched,
}: EspDestinationsSectionProps) {
  return (
    <FormSection
      title="ESP destinations"
      desc="Each campaign subscribes accepted leads to brand lists across Maropost, Iterable and Beehiiv (Q56). Enabled rows with a list id subscribe each accepted lead; rows without one are skipped. A subscription result (including suppressed) is an outcome, not a journey failure."
    >
      {/* 067-05 (D5): warn, don't block — an enabled Iterable row with no
          (or non-numeric) journey id would otherwise silently skip that
          journey at send time. */}
      {espDestinations.some(
        (d) => d.provider === 'iterable' && !/^\d+$/.test(d.listIdentifier.trim())
      ) && (
        <div className="sm:col-span-2">
          <Banner tone="warn">
            An Iterable row has no journey id — leads will not enter that journey. Remove the row
            and add it again to fill the default, or type the id.
          </Banner>
        </div>
      )}
      <div className="sm:col-span-2 overflow-hidden rounded-lg hairline">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-line bg-paper text-xs uppercase tracking-wide text-faint">
              <th className="px-4 py-2 font-semibold">Provider</th>
              <th className="px-4 py-2 font-semibold">Brand list</th>
              <th className="px-4 py-2 font-semibold">Full name</th>
              <th className="px-4 py-2 font-semibold">List id</th>
              <th className="px-4 py-2 text-center font-semibold">Enabled</th>
              <th className="px-4 py-2 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* HF-075 (QA-04): the table offers the four current brand
                lists plus any legacy (DMB / Super Investors) row a
                campaign already has, so an existing legacy row stays
                visible and removable instead of hiding while the saved
                payload (built from all of the form's rows, not this
                catalog) still carries it. */}
            {[
              ...BRAND_LIST_OPTIONS.filter(
                (bl) => !bl.offeredFor || bl.offeredFor.some((s) => s === campaignStyle)
              ).map((bl) => ({ ...bl, isLegacy: false as const })),
              ...LEGACY_BRAND_LIST_OPTIONS.filter((bl) =>
                espDestinations.some(
                  (d) => d.provider === bl.provider && d.brandList === bl.brandList
                )
              ).map((bl) => ({ ...bl, isLegacy: true as const })),
            ].map((bl) => {
              const active = espDestinations.some(
                (d) => d.provider === bl.provider && d.brandList === bl.brandList
              );
              // A12 (034-05): the Maropost TT row renders a second <tr> when
              // active, carrying the transactional pair controls.
              const isMaropostTT = bl.provider === 'maropost' && bl.brandList === 'tt';
              // 054-08: every enabled row's form-state row — the List id
              // input binds to it; the A12 sub-row below stays TT-only.
              const activeRow = active
                ? espDestinations.find(
                    (d) => d.provider === bl.provider && d.brandList === bl.brandList
                  )
                : undefined;
              const rowData = isMaropostTT && active ? activeRow : undefined;
              return (
                <Fragment key={`${bl.provider}-${bl.brandList}`}>
                  <tr className="border-b border-line/70">
                    <td className="px-4 py-2.5">{bl.provider}</td>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold">{bl.brandList}</td>
                    <td className="px-4 py-2.5 text-muted">
                      <span>{bl.label}</span>
                      {bl.isLegacy && (
                        <span className="ml-1.5 text-faint">(no longer offered)</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {/* 054-08: the List id is exactly the defect fix — a
                          row saved with an empty id is skipped at send time
                          (missing_list_identifier). Enabled rows get an
                          input; disabled rows have nothing to identify. */}
                      {activeRow ? (
                        <Input
                          className="w-32"
                          value={activeRow.listIdentifier}
                          aria-label={`List id for ${bl.provider} ${bl.brandList}`}
                          onChange={(e) => {
                            onTouched();
                            const value = e.target.value;
                            onRowsChange(
                              espDestinations.map((d) =>
                                d.provider === bl.provider && d.brandList === bl.brandList
                                  ? { ...d, listIdentifier: value }
                                  : d
                              )
                            );
                          }}
                        />
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">{active ? '✓' : '--'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // 054-08: any manual Add/Remove latches the seed
                          // off — a removed default never comes back.
                          onTouched();
                          if (active) {
                            // Remove
                            onRowsChange(
                              espDestinations.filter(
                                (d) => !(d.provider === bl.provider && d.brandList === bl.brandList)
                              )
                            );
                          } else {
                            // Add — transactional pair initialised empty (A12).
                            // 067-05 (D5, Manny 2026-09-03): an Iterable
                            // row's id is prefilled from the catalog
                            // default; other providers stay empty.
                            onRowsChange([
                              ...espDestinations,
                              {
                                id: newRowId(),
                                provider: bl.provider,
                                brandList: bl.brandList,
                                listIdentifier: bl.defaultListIdentifier ?? '',
                                fieldMappingsJson: '{}',
                                transactionalCampaignId: '',
                                transactionalOn: '' as const,
                                // HF-084: the journey UI is hidden and no
                                // longer prefilled — an added TT row
                                // starts empty.
                                maropostJourneyId: '',
                                maropostTriggerUid: '',
                                maropostTags: '',
                              },
                            ]);
                          }
                        }}
                      >
                        {active ? 'Remove' : 'Add'}
                      </Button>
                    </td>
                  </tr>
                  {/* A12 (034-05): transactional controls — Maropost TT only,
                      rendered when the row is enabled. Other providers get
                      nothing (Q85 (e) was No). */}
                  {isMaropostTT && active && rowData && (
                    <tr className="border-b border-line/70 bg-paper/50">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="flex flex-wrap gap-4 items-end">
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-faint">
                              Transactional campaign id
                            </span>
                            <Input
                              className="w-48"
                              value={rowData.transactionalCampaignId ?? ''}
                              placeholder="Leave blank to disable"
                              onChange={(e) =>
                                onRowsChange(
                                  espDestinations.map((d) =>
                                    d.provider === 'maropost' && d.brandList === 'tt'
                                      ? { ...d, transactionalCampaignId: e.target.value }
                                      : d
                                  )
                                )
                              }
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-faint">
                              Send transactional on
                            </span>
                            <Select
                              value={rowData.transactionalOn ?? ''}
                              onChange={(e) =>
                                onRowsChange(
                                  espDestinations.map((d) =>
                                    d.provider === 'maropost' && d.brandList === 'tt'
                                      ? {
                                          ...d,
                                          transactionalOn: e.target.value as
                                            '' | 'success' | 'valid',
                                        }
                                      : d
                                  )
                                )
                              }
                            >
                              <option value="">Default (valid)</option>
                              <option value="success">Success</option>
                              <option value="valid">Valid</option>
                            </Select>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-faint">
        Maropost list id · Iterable workflow id · Beehiiv publication id. Empty rows are skipped at
        send time.
      </p>
    </FormSection>
  );
}
